/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Common/StackedColumnChartHelper" />
import * as ChartSeries from "Analytics/Shared/ChartSeries/ChartSeries";
import { gridLineColor } from "Analytics/Shared/Common/Constants";
import { TrendChartHelper } from "Analytics/Shared/TrendChart/Common/TrendChartHelper";
import { ITrendChartAggregateData, ITrendChartCustomData, ITrendChartData } from "Analytics/Shared/TrendChart/Common/Types";
import { maxItemsToShowInCharts } from "Pipeline/Analytics/Common/Constants";
import { PipelineFailureTrendSeries } from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Common/PipelineOutcomeChartSeries";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Resources";
import { AxisOptions, ChartProps, ChartTypesConstants, DataSeries, LabelFormatModes, TooltipLineItem } from "VSS/Features/Charts/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { colors } from "Pipeline/Analytics/Common/Constants";

export class StackedColumnChartHelper {
    private _trendChartHelper: TrendChartHelper;

    constructor(private pageContext: IVssPageContext) {
        this._trendChartHelper = new TrendChartHelper();
    }

    public getChartOptions(chartData: ITrendChartData): ChartProps {
        const dataSeries: DataSeries[] = [];
        let xAxisLabelValues: string[] = [];
        const chartTypes: string[] = [];

        const trendBy = chartData.confValues ? chartData.confValues.trendBy : undefined;
        const period = chartData.confValues ? chartData.confValues.period : undefined;

        let primaryChartData = chartData.primaryChartData;

        let customData: (ITrendChartCustomData | undefined)[] | undefined;
        primaryChartData = this._trendChartHelper.adjustDataForAllStackBySeries(chartData.primaryChartData, trendBy, period);
        const primaryChartSeries: ChartSeries.ChartSeries = this._getPrimaryChartSeries(
            this._getSeriesData(chartData.primaryChartData[Object.keys(primaryChartData)[0]])
        );

        const primaryChartSeriesLength = Object.keys(primaryChartData).length;

        if (primaryChartSeriesLength === 1) {
            const primaryChartSingleData = primaryChartData[Object.keys(primaryChartData)[0]];
            customData = this._getCustomData(primaryChartSingleData);

            const currChartData: { aggrValues: number[]; dates: string[] } = { aggrValues: [], dates: [] };
            primaryChartSingleData.forEach(d => (currChartData.aggrValues.push(d.aggrValue), currChartData.dates.push(d.date)));
            dataSeries.push({
                name: primaryChartSeries.name,
                color: primaryChartSeries.color,
                data: currChartData.aggrValues,
                customData: customData
            });
            xAxisLabelValues = currChartData.dates;
        } else {
            xAxisLabelValues = this._trendChartHelper.getStackedChartData(
                primaryChartData,
                customData,
                dataSeries,
                chartTypes,
                maxItemsToShowInCharts,
                colors
            );
        }

        const chartOptions = {
            title: Resources.FailureTrendChartAriaLabelText,
            pageContext: this.pageContext,
            chartType: ChartTypesConstants.StackedColumn,
            suppressAnimation: true,
            legend: {
                enabled: false
            },
            specializedOptions: {
                includeMarkers: true
            },

            xAxis: {
                labelFormatMode: LabelFormatModes.DateTime_DayInMonth,
                labelsEnabled: true,
                labelValues: xAxisLabelValues,
                formatAccessibleLabelsUsingAxisCategories: true
            } as AxisOptions,
            yAxis: {
                labelFormatMode: LabelFormatModes.Linear,
                labelsEnabled: true,
                markingsEnabled: false,
                title: primaryChartSeries.name,
                gridLineOptions: {
                    enabled: true,
                    gridLineColor: gridLineColor
                }
            } as AxisOptions,
            series: dataSeries
        } as ChartProps;

        return {
            ...chartOptions
        };
    }

    private _getSeriesData(aggregatedData: ITrendChartAggregateData[]): number[] {
        return aggregatedData ? aggregatedData.map(d => d.aggrValue) : [];
    }

    private _getPrimaryChartSeries(seriesData: number[]): ChartSeries.ChartSeries {
        return new PipelineFailureTrendSeries(seriesData);
    }

    private _getCustomData(aggregatedData: ITrendChartAggregateData[]): (ITrendChartCustomData | undefined)[] {
        return aggregatedData ? aggregatedData.map(d => d.customData) : [];
    }
}
